import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getPhasePhotos, uploadPhoto, deletePhoto } from '../../services/photoService';
import { getCase, getCasePhases } from '../../services/caseService';
import { getPatient } from '../../services/patientService';
import { getCategoryById, getAllCategories } from '../../constants/dawsonCategories';
import { getPhaseConfig, getPhaseCompletionStatus, isPhotoRequired } from '../../constants/phasePhotoConfigs';
import PhotoUploader from './PhotoUploader';
import PhotoViewer from './PhotoViewer';

const PhasePhotoManager = () => {
  const { patientId, caseId, phaseId } = useParams();
  const { userProfile } = useAuth();

  const [patient, setPatient] = useState(null);
  const [treatmentCase, setTreatmentCase] = useState(null);
  const [phase, setPhase] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploader, setShowUploader] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [uploadingCategory, setUploadingCategory] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'checklist'

  useEffect(() => {
    loadData();
  }, [patientId, caseId, phaseId, userProfile]);

  const loadData = async () => {
    if (!userProfile) return;
    try {
      setLoading(true);
      setError('');

      const [patientData, caseData, phasesData, photosData] = await Promise.all([
        getPatient(userProfile.practiceId, userProfile.id, patientId),
        getCase(userProfile.practiceId, userProfile.id, patientId, caseId),
        getCasePhases(userProfile.practiceId, patientId, caseId),
        getPhasePhotos(userProfile.practiceId, patientId, caseId, phaseId)
      ]);

      setPatient(patientData);
      setTreatmentCase(caseData);

      const currentPhase = phasesData.find(p => p.id === phaseId);
      setPhase(currentPhase);
      setPhotos(photosData);
    } catch (err) {
      console.error('Error loading phase data:', err);
      setError('Failed to load phase data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (file, category) => {
    if (!userProfile) return;
    try {
      const newPhoto = await uploadPhoto(
        userProfile.practiceId,
        userProfile.id,
        patientId,
        caseId,
        phaseId,
        file,
        category
      );
      setPhotos(prev => [...prev, newPhoto]);
      setShowUploader(false);
      setUploadingCategory(null);
    } catch (err) {
      console.error('Error uploading photo:', err);
      throw err;
    }
  };

  const handleDelete = async (photo) => {
    if (!userProfile) return;
    if (!window.confirm('Are you sure you want to delete this photo?')) return;

    try {
      await deletePhoto(
        userProfile.practiceId,
        userProfile.id,
        patientId,
        caseId,
        phaseId,
        photo.id,
        photo.storagePath
      );
      setPhotos(prev => prev.filter(p => p.id !== photo.id));
      setSelectedPhoto(null);
    } catch (err) {
      console.error('Error deleting photo:', err);
      setError('Failed to delete photo');
    }
  };

  const openUploaderForCategory = (categoryId) => {
    setUploadingCategory(categoryId);
    setShowUploader(true);
  };

  const phaseConfig = phase ? getPhaseConfig(phase.phaseType) : null;
  const uploadedCategoryIds = photos.map(p => p.category);
  const completionStatus = phase
    ? getPhaseCompletionStatus(phase.phaseType, uploadedCategoryIds)
    : null;

  const getPhotoForCategory = useCallback((categoryId) => {
    return photos.find(p => p.category === categoryId);
  }, [photos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !phase) {
    return (
      <div className="p-6">
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error || 'Phase not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center space-x-2 text-sm text-gray-500">
        <Link to={`/patients/${patientId}`} className="hover:text-blue-600">
          {patient ? `${patient.firstName} ${patient.lastName}` : 'Patient'}
        </Link>
        <span>/</span>
        <Link to={`/patients/${patientId}/cases/${caseId}`} className="hover:text-blue-600">
          {treatmentCase?.name || 'Case'}
        </Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">{phaseConfig?.name || 'Photos'}</span>
      </nav>

      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {phaseConfig?.name || 'Phase Photos'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">{phaseConfig?.description}</p>
        </div>
        <div className="flex items-center space-x-3">
          <div className="flex rounded-md shadow-sm">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 text-sm font-medium rounded-l-md border ${
                viewMode === 'grid'
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Grid
            </button>
            <button
              onClick={() => setViewMode('checklist')}
              className={`px-3 py-2 text-sm font-medium rounded-r-md border-t border-r border-b ${
                viewMode === 'checklist'
                  ? 'bg-blue-50 text-blue-700 border-blue-300'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Checklist
            </button>
          </div>
          <button
            onClick={() => { setUploadingCategory(null); setShowUploader(true); }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Upload Photo
          </button>
        </div>
      </div>

      {/* Completion Progress */}
      {completionStatus && (
        <div className="bg-white shadow rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Phase Completion: {completionStatus.uploaded} / {completionStatus.total} photos
            </span>
            <span className="text-sm font-medium text-gray-700">
              {completionStatus.progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${completionStatus.progress}%` }}
            ></div>
          </div>
          <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
            <span>
              Required: {completionStatus.required - completionStatus.missingRequired.length} / {completionStatus.required}
            </span>
            {completionStatus.missingRequired.length > 0 && (
              <span className="text-amber-600">
                {completionStatus.missingRequired.length} required photo(s) missing
              </span>
            )}
          </div>
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {phaseConfig?.photos.map((catKey) => {
            const category = getCategoryById(catKey.toLowerCase ? catKey.toLowerCase() : catKey);
            const catId = category?.id || catKey;
            const photo = getPhotoForCategory(catId);
            const required = phase ? isPhotoRequired(phase.phaseType, catId) : false;

            return (
              <div
                key={catId}
                className={`relative rounded-lg border-2 overflow-hidden group ${
                  photo
                    ? 'border-green-300 bg-green-50'
                    : required
                    ? 'border-amber-300 bg-amber-50'
                    : 'border-gray-200 bg-gray-50'
                }`}
              >
                {photo ? (
                  <div className="aspect-square relative">
                    <img
                      src={photo.downloadURL}
                      alt={category?.name || catId}
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <button
                        onClick={() => setSelectedPhoto(photo)}
                        className="bg-white text-gray-800 px-3 py-1.5 rounded-md text-sm font-medium mr-2 shadow"
                      >
                        View
                      </button>
                      <button
                        onClick={() => handleDelete(photo)}
                        className="bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-medium shadow"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="absolute top-2 right-2">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-500">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    className="aspect-square flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors p-4"
                    onClick={() => openUploaderForCategory(catId)}
                  >
                    <svg className="w-10 h-10 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-xs text-gray-500 text-center">Click to upload</span>
                  </div>
                )}
                <div className="p-2 bg-white border-t">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-medium text-gray-700 truncate">
                      {category?.name || catId}
                    </p>
                    {required && !photo && (
                      <span className="text-xs text-amber-600 font-medium">Required</span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 truncate">{category?.type}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Checklist View */}
      {viewMode === 'checklist' && (
        <div className="bg-white shadow rounded-lg divide-y divide-gray-200">
          {['extraoral', 'intraoral', 'closeup'].map((type) => {
            const typeCategories = getAllCategories().filter(c => c.type === type);
            const phasePhotoIds = (phaseConfig?.photos || []).map(k => k.toLowerCase ? k.toLowerCase() : k);

            return (
              <div key={type}>
                <div className="px-6 py-3 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                    {type} Photos
                  </h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {typeCategories
                    .filter(cat => phasePhotoIds.includes(cat.id))
                    .map((category) => {
                      const photo = getPhotoForCategory(category.id);
                      const required = phase ? isPhotoRequired(phase.phaseType, category.id) : false;

                      return (
                        <div key={category.id} className="px-6 py-3 flex items-center justify-between hover:bg-gray-50">
                          <div className="flex items-center min-w-0">
                            <div className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                              photo ? 'bg-green-500 border-green-500' : 'border-gray-300'
                            }`}>
                              {photo && (
                                <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-900">
                                {category.order}. {category.name}
                                {required && <span className="text-amber-500 ml-1">*</span>}
                              </p>
                              <p className="text-xs text-gray-500 truncate">{category.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2 ml-4">
                            {photo ? (
                              <>
                                <img
                                  src={photo.downloadURL}
                                  alt={category.name}
                                  className="w-10 h-10 rounded object-cover cursor-pointer"
                                  onClick={() => setSelectedPhoto(photo)}
                                />
                                <button
                                  onClick={() => openUploaderForCategory(category.id)}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  Replace
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => openUploaderForCategory(category.id)}
                                className="inline-flex items-center px-3 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                              >
                                Upload
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phase Notes */}
      {phaseConfig?.notes && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Phase Guidelines</h3>
          <ul className="list-disc list-inside text-sm text-blue-700 space-y-1">
            {phaseConfig.notes.map((note, i) => (
              <li key={i}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Upload Modal */}
      {showUploader && (
        <PhotoUploader
          onUpload={handleUpload}
          onClose={() => { setShowUploader(false); setUploadingCategory(null); }}
          preselectedCategory={uploadingCategory}
          phaseType={phase?.phaseType}
        />
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <PhotoViewer
          photo={selectedPhoto}
          photos={photos}
          onClose={() => setSelectedPhoto(null)}
          onDelete={handleDelete}
          onNavigate={setSelectedPhoto}
          patientId={patientId}
          caseId={caseId}
          phaseId={phaseId}
        />
      )}
    </div>
  );
};

export default PhasePhotoManager;
